import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import type { Section } from "../../types/api";

interface Props {
  sections: Section[];
  activeId?: string;
  collapsed: Record<string, boolean>;
  onJump: (id: string) => void;
  onToggleCollapse: (id: string) => void;
}

export function TocPanel({ sections, activeId, collapsed, onJump, onToggleCollapse }: Props) {
  return (
    <nav aria-label="Tabla de contenidos">
      <div className="mb-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-(--text-tertiary)">
        <span>Contenido</span>
        <span className="flex gap-0.5">
          <button
            type="button"
            className="rounded p-0.5 hover:bg-(--bg-hover)"
            onClick={() => onToggleCollapse("all-h2")}
            aria-label="Plegar todo"
            title="Plegar todo"
          >
            <ChevronRight size={11} />
          </button>
          <button
            type="button"
            className="rounded p-0.5 hover:bg-(--bg-hover)"
            onClick={() => onToggleCollapse("expand-all")}
            aria-label="Desplegar todo"
            title="Desplegar todo"
          >
            <ChevronDown size={11} />
          </button>
        </span>
      </div>
      <ol className="space-y-0.5 text-sm">
        {sections.map((sec) => {
          const isActive = sec.id === activeId;
          const label = sec.heading_path[sec.heading_path.length - 1] ?? "(sin título)";
          return (
            <li key={sec.id}>
              <button
                type="button"
                onClick={() => onJump(sec.id)}
                className={cn(
                  "w-full text-left rounded px-1.5 py-1 transition-colors flex items-center gap-1",
                  isActive
                    ? "bg-(--accent-50) text-(--accent-700) font-medium dark:bg-(--accent-100) dark:text-(--accent-50)"
                    : "text-(--text-secondary) hover:bg-(--bg-hover)",
                )}
                style={{ paddingLeft: 8 + Math.max(0, sec.heading_level - 1) * 10 }}
              >
                {sec.heading_level === 2 && (
                  // biome-ignore lint/a11y/useSemanticElements: nested button not allowed inside button
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleCollapse(sec.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        onToggleCollapse(sec.id);
                      }
                    }}
                    className="cursor-pointer text-(--text-tertiary) hover:text-(--text-primary)"
                  >
                    {collapsed[sec.id] ? <ChevronRight size={11} /> : <ChevronDown size={11} />}
                  </span>
                )}
                <span className="truncate" title={label}>
                  {label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
